import { useAnalytics } from "../hooks/useAnalytics";
import { useRef, useCallback, useState, MouseEvent as ReactMouseEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";

const HeaderWrapper = styled.header`
  background-color: rgba(24, 26, 27, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoGroup = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
`;

const LogoImg = styled.img`
  height: 32px;
  width: 32px;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.5px;
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? "flex" : "none")};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    background-color: var(--bg-secondary);
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
  }
`;

const NavLink = styled(Link)`
  color: var(--text-secondary);
  font-size: 0.95rem;
  transition: color 0.2s;

  &:hover {
    color: var(--accent-color);
  }
`;

const ExternalLink = styled.a`
  color: var(--text-secondary);
  font-size: 0.95rem;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: var(--accent-color);
  }
`;

const BurgerButton = styled.button`
  display: none;
  color: var(--text-primary);
  font-size: 1.5rem;

  @media (max-width: 768px) {
    display: block;
  }
`;

const GithubIcon = () => (
  <svg height="20" width="20" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
  </svg>
);

const quackAnimation = keyframes`
  0% { transform: scale(1) rotate(0); }
  50% { transform: scale(1.2) rotate(15deg); }
  100% { transform: scale(1) rotate(0); }
`;

const QuackDuck = styled.button`
  font-size: 1.5rem;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0.5rem;
  transition: transform 0.2s;

  @media (max-width: 768px) {
    display: none;
  }

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }

  &.quacking {
    animation: ${quackAnimation} 0.3s ease;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const location = useLocation();
  const { trackDuckQuack, trackDocumentationClick, trackGithubClick } = useAnalytics();

  const scrollToConcept = useCallback(() => {
    const conceptSection = document.getElementById("concept");
    if (conceptSection) {
      conceptSection.scrollIntoView({ behavior: "smooth" });
      return true;
    }
    return false;
  }, []);

  const handleQuack = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/static/quack.mp3");
      audioRef.current.volume = 0.5;
    }

    const audio = audioRef.current;
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.log("Quack blocked:", err.message);
    });

    const duck = document.getElementById("quack-duck");
    if (duck) {
      duck.classList.add("quacking");
      setTimeout(() => duck.classList.remove("quacking"), 300);
    }

    trackDuckQuack();
  }, [trackDuckQuack]);

  const handleDocsClick = useCallback(() => {
    trackDocumentationClick("header_nav");
    setIsMenuOpen(false);
  }, [trackDocumentationClick]);

  const handleGithubLink = useCallback(() => {
    trackGithubClick("header_nav");
  }, [trackGithubClick]);

  const handleHomeClick = useCallback(
    (event?: ReactMouseEvent<HTMLAnchorElement>) => {
      if (location.pathname === "/") {
        event?.preventDefault();
        if (location.hash) {
          window.history.pushState({}, "", "/");
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      setIsMenuOpen(false);
    },
    [location.pathname, location.hash],
  );

  const handleConceptClick = useCallback(
    (event?: ReactMouseEvent<HTMLAnchorElement>) => {
      if (location.pathname === "/" && location.hash === "#concept") {
        const didScroll = scrollToConcept();
        if (didScroll) {
          event?.preventDefault();
        }
      }
      setIsMenuOpen(false);
    },
    [location.pathname, location.hash, scrollToConcept],
  );

  return (
    <HeaderWrapper>
      <Container>
        <LogoGroup to="/" onClick={handleHomeClick}>
          <LogoImg
            src="/static/logo-112.png"
            srcSet="/static/logo-64.png 64w, /static/logo-112.png 112w, /static/logo-224.png 224w"
            sizes="32px"
            alt="Kuack Logo"
            width="32"
            height="32"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <LogoText>Kuack</LogoText>
        </LogoGroup>

        <BurgerButton onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? "✕" : "☰"}</BurgerButton>

        <Nav $isOpen={isMenuOpen}>
          <NavLink to="/" onClick={handleHomeClick}>
            Home
          </NavLink>
          <NavLink to="/#concept" onClick={handleConceptClick}>
            Concept
          </NavLink>
          <NavLink to="/docs" onClick={handleDocsClick}>
            Documentation
          </NavLink>
          <ExternalLink
            href="https://github.com/kuack-io/kuack"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGithubLink}
          >
            <GithubIcon />
            GitHub
          </ExternalLink>
          <QuackDuck id="quack-duck" onClick={handleQuack} title="Quack!">
            🦆
          </QuackDuck>
        </Nav>
      </Container>
    </HeaderWrapper>
  );
};

export default Header;
